import { Box, Grid } from '@mui/material';
import { Incorporation1120sOnly } from 'components/ProgressTracker/Incorporation/Incorporate1120sOnly';
import { AccountVerificationComponent } from 'components/ProgressTracker/AccountVerification/AccountVerificationComponent';
import { FormationsPrimaryButton } from 'components/common/Buttons';
import {
  IAccount,
  ProgressTrackerStages,
  ProgressTrackerStatus,
} from 'models/account';
import { useMediaBreakpoints } from 'hooks';
import { useActivateBusiness, useNavigate } from 'hooks/api';
import { showErrorToast, showToastOnError } from 'components/toast/showToast';
import { Routes } from 'fnRoutes';
import { queryClient } from 'states/reactQueryClient';
import React, { useMemo, useState } from 'react';
import {
  getProgressTrackerStageStatus,
  isProgressTrackerStageCompleted,
} from 'components/ProgressTracker/Incorporation/state';
import { Business } from 'models/business';

interface Props {
  account: IAccount;
  business: Business;
}

export const ProgressTracker1120sOnly = ({ account, business }: Props) => {
  const { isMobile, isDesktop } = useMediaBreakpoints();
  const { activateBusinessAsync: activate, isLoading } = useActivateBusiness(
    business.id,
  );
  const navigate = useNavigate();

  const [isExpandedIncorporationSection, setIsExpandedIncorporationSection] =
    useState(false);
  const [
    expandedAccountVerificationSection,
    setExpandedAccountVerificationSection,
  ] = useState<string | false>(false);

  const handleExpandIncorporationSection = () =>
    setIsExpandedIncorporationSection(!isExpandedIncorporationSection);
  const handleExpandAccountVerificationSection =
    (panel: string) => (event: React.ChangeEvent<{}>, isExpanded: boolean) => {
      setExpandedAccountVerificationSection(isExpanded ? panel : false);
    };

  const onContinueToAccountVerification = () => {
    setIsExpandedIncorporationSection(false);
    setExpandedAccountVerificationSection('account-verification');
  };

  const onCollapseAllSections = () => {
    setIsExpandedIncorporationSection(false);
    setExpandedAccountVerificationSection(false);
  };

  const finishAllSteps = useMemo(() => {
    const statusUploadIdentifyProof = getProgressTrackerStageStatus(
      business,
      ProgressTrackerStages.UploadIdentityProof,
    );
    const statusUploadLastYearTaxStatement = getProgressTrackerStageStatus(
      business,
      ProgressTrackerStages.UploadLastYearTaxStatement,
    );
    const statusDocumentOfIncorporation = getProgressTrackerStageStatus(
      business,
      ProgressTrackerStages.DocumentOfIncorporation,
    );
    return (
      isProgressTrackerStageCompleted(
        business,
        ProgressTrackerStages.PersonalDetails,
      ) &&
      isProgressTrackerStageCompleted(
        business,
        ProgressTrackerStages.CompanyDetails,
      ) &&
      isProgressTrackerStageCompleted(
        business,
        ProgressTrackerStages.AddressDetails,
      ) &&
      (statusUploadIdentifyProof === ProgressTrackerStatus.InProgress ||
        statusUploadIdentifyProof === ProgressTrackerStatus.Completed) &&
      (statusUploadLastYearTaxStatement === ProgressTrackerStatus.InProgress ||
        statusUploadLastYearTaxStatement === ProgressTrackerStatus.Completed) &&
      (statusDocumentOfIncorporation === ProgressTrackerStatus.InProgress ||
        statusDocumentOfIncorporation === ProgressTrackerStatus.Completed)
    );
  }, [business]);

  const handleGetStarted = async () => {
    if (!finishAllSteps) {
      showErrorToast(
        'Please complete all the steps in the Create Your Account and Verify Your Account sections before going to your dashboard.',
      );
      return;
    }
    try {
      await activate();
      await Promise.all([
        queryClient.invalidateQueries('currentAccount'),
        queryClient.invalidateQueries(['account', account.id]),
        queryClient.invalidateQueries(['currentCompanyList']),
        queryClient.invalidateQueries(['business', business.id]),
      ]);
      navigate(Routes.HOME());
    } catch (e) {
      showToastOnError(e);
    }
  };

  return (
    <Box
      sx={{
        width: 'auto',
        px: isMobile ? 2 : 9,
        py: 2,
        mt: 10,
      }}
    >
      <Incorporation1120sOnly
        isDesktop={isDesktop}
        isExpanded={isExpandedIncorporationSection}
        expandHandler={handleExpandIncorporationSection}
        onContinueToAccountVerification={onContinueToAccountVerification}
      />
      <AccountVerificationComponent
        handleExpand={handleExpandAccountVerificationSection}
        expanded={expandedAccountVerificationSection}
        onContinueToNextSection={onCollapseAllSections}
      />
      <Grid container justifyContent="center">
        <FormationsPrimaryButton
          size="large"
          onClick={handleGetStarted}
          isLoading={isLoading}
        >
          Go to My Dashboard
        </FormationsPrimaryButton>
      </Grid>
    </Box>
  );
};
